import type {VesselParticulars} from './_models'

export type VesselParticularFieldType = 'text' | 'textarea' | 'number' | 'date'

export type VesselParticularFieldConfig = {
  key: keyof VesselParticulars
  label: string
  type: VesselParticularFieldType
  unit?: string
  readOnly?: boolean
}

export type VesselParticularSectionConfig = {
  key: string
  label: string
  fields: VesselParticularFieldConfig[]
}

export const VESSEL_PARTICULAR_SECTIONS: VesselParticularSectionConfig[] = [
  {
    key: 'identity',
    label: 'Identity & Registration',
    fields: [
      {key: 'vesselName', label: 'Vessel Name', type: 'text', readOnly: true},
      {key: 'vesselType', label: 'Vessel Type', type: 'text', readOnly: true},
      {key: 'exName', label: 'Ex Name', type: 'text'},
      {key: 'callSign', label: 'Call Sign', type: 'text'},
      {key: 'flag', label: 'Flag', type: 'text'},
      {key: 'portOfRegistry', label: 'Port of Registry', type: 'text'},
      {key: 'officialNumber', label: 'Official Number', type: 'text'},
      {key: 'imoNumber', label: 'IMO Number', type: 'text', readOnly: true},
      {key: 'classSociety', label: 'Class Society', type: 'text'},
      {key: 'classNotation', label: 'Class Notation', type: 'text'},
      {key: 'pAndIClub', label: 'P&I Club', type: 'text'},
      {key: 'classNumber', label: 'Class Number', type: 'text'},
      {key: 'keelLaidDate', label: 'Keel Laid Date', type: 'date'},
      {key: 'launchedDate', label: 'Launched Date', type: 'date'},
      {key: 'deliveredDate', label: 'Delivered Date', type: 'date'},
      {key: 'shipyard', label: 'Shipyard', type: 'text'},
      {key: 'lastDryDockDate', label: 'Last Dry Dock Date', type: 'date'},
      {key: 'lastDryDockLocation', label: 'Last Dry Dock Location', type: 'text'},
    ],
  },
  {
    key: 'ownership',
    label: 'Ownership & Dimensions',
    fields: [
      {key: 'registeredOwner', label: 'Registered Owner', type: 'textarea'},
      {key: 'technicalOperator', label: 'Technical Operator', type: 'textarea'},
      {key: 'commercialOperator', label: 'Commercial Operator', type: 'textarea'},
      {key: 'lengthOverallM', label: 'Length Overall', type: 'number', unit: 'm'},
      {key: 'lengthBetweenPerpendicularsM', label: 'Length Between Perpendiculars', type: 'number', unit: 'm'},
      {key: 'breadthExtremeM', label: 'Breadth Extreme', type: 'number', unit: 'm'},
      {key: 'depthMouldedM', label: 'Depth Moulded', type: 'number', unit: 'm'},
      {key: 'maximumHeightM', label: 'Maximum Height', type: 'number', unit: 'm'},
      {key: 'bridgeFrontToBowM', label: 'Bridge Front To Bow', type: 'number', unit: 'm'},
      {key: 'bridgeFrontToSternM', label: 'Bridge Front To Stern', type: 'number', unit: 'm'},
      {key: 'bridgeFrontToManifoldM', label: 'Bridge Front To Manifold', type: 'number', unit: 'm'},
    ],
  },
  {
    key: 'tonnage',
    label: 'Tonnage & Load Line',
    fields: [
      {key: 'grossTonnage', label: 'Gross Tonnage', type: 'number'},
      {key: 'netTonnage', label: 'Net Tonnage', type: 'number'},
      {key: 'suezGrossTonnage', label: 'Suez Gross Tonnage', type: 'number'},
      {key: 'suezNetTonnage', label: 'Suez Net Tonnage', type: 'number'},
      {key: 'panamaGrossTonnage', label: 'Panama Gross Tonnage', type: 'number'},
      {key: 'panamaNetTonnage', label: 'Panama Net Tonnage', type: 'number'},
      {key: 'suezId', label: 'Suez ID', type: 'text'},
      {key: 'tropicalFreeboardM', label: 'Tropical Freeboard', type: 'number', unit: 'm'},
      {key: 'tropicalDraftM', label: 'Tropical Draft', type: 'number', unit: 'm'},
      {key: 'tropicalDeadweightMt', label: 'Tropical Deadweight', type: 'number', unit: 'mt'},
      {key: 'summerFreeboardM', label: 'Summer Freeboard', type: 'number', unit: 'm'},
      {key: 'summerDraftM', label: 'Summer Draft', type: 'number', unit: 'm'},
      {key: 'summerDeadweightMt', label: 'Summer Deadweight', type: 'number', unit: 'mt'},
      {key: 'winterFreeboardM', label: 'Winter Freeboard', type: 'number', unit: 'm'},
      {key: 'winterDraftM', label: 'Winter Draft', type: 'number', unit: 'm'},
      {key: 'winterDeadweightMt', label: 'Winter Deadweight', type: 'number', unit: 'mt'},
      {key: 'normalBallastDraftM', label: 'Normal Ballast Draft', type: 'number', unit: 'm'},
      {key: 'normalBallastDisplacementMt', label: 'Normal Ballast Displacement', type: 'number', unit: 'mt'},
      {key: 'tropicalDisplacementMt', label: 'Tropical Displacement', type: 'number', unit: 'mt'},
      {key: 'summerDisplacementMt', label: 'Summer Displacement', type: 'number', unit: 'mt'},
      {key: 'winterDisplacementMt', label: 'Winter Displacement', type: 'number', unit: 'mt'},
      {key: 'lightWeightMt', label: 'Light Weight', type: 'number', unit: 'mt'},
      {key: 'freshWaterAllowanceMm', label: 'Fresh Water Allowance', type: 'number', unit: 'mm'},
      {key: 'tonnesPerCentimetre', label: 'Tonnes Per Centimetre', type: 'number'},
      {key: 'momentToChangeTrimTm', label: 'Moment To Change Trim', type: 'number', unit: 't-m'},
    ],
  },
  {
    key: 'machinery',
    label: 'Machinery & Propulsion',
    fields: [
      {key: 'mainEngineMaker', label: 'Main Engine Maker', type: 'text'},
      {key: 'mainEngineModel', label: 'Main Engine Model', type: 'text'},
      {key: 'mainEngineMcr', label: 'Main Engine MCR', type: 'text'},
      {key: 'mainEngineNcr', label: 'Main Engine NCR', type: 'text'},
      {key: 'mainEngineConsumption', label: 'Main Engine Consumption', type: 'text'},
      {key: 'generatorDetails', label: 'Generator Details', type: 'textarea'},
      {key: 'emergencyGeneratorDetails', label: 'Emergency Generator Details', type: 'textarea'},
      {key: 'boilerDetails', label: 'Boiler Details', type: 'textarea'},
      {key: 'steeringGearDetails', label: 'Steering Gear Details', type: 'textarea'},
      {key: 'propellerDetails', label: 'Propeller Details', type: 'textarea'},
      {key: 'rudderDetails', label: 'Rudder Details', type: 'textarea'},
    ],
  },
  {
    key: 'capacities',
    label: 'Capacities & Deck Equipment',
    fields: [
      {key: 'fuelOilTankCapacityM3', label: 'Fuel Oil Tank Capacity', type: 'number', unit: 'm3'},
      {key: 'dieselOilTankCapacityM3', label: 'Diesel Oil Tank Capacity', type: 'number', unit: 'm3'},
      {key: 'freshWaterCapacityM3', label: 'Fresh Water Capacity', type: 'number', unit: 'm3'},
      {key: 'totalBunkerCapacityM3', label: 'Total Bunker Capacity', type: 'number', unit: 'm3'},
      {key: 'windlassDetails', label: 'Windlass Details', type: 'textarea'},
      {key: 'winchDetails', label: 'Winch Details', type: 'textarea'},
      {key: 'anchorDetails', label: 'Anchor Details', type: 'textarea'},
      {key: 'anchorChainDetails', label: 'Anchor Chain Details', type: 'textarea'},
      {key: 'mooringWireDetails', label: 'Mooring Wire Details', type: 'textarea'},
      {key: 'emergencyTowingDetails', label: 'Emergency Towing Details', type: 'textarea'},
      {key: 'cargoOrDeckCraneDetails', label: 'Deck/Cargo Crane Details', type: 'textarea'},
      {key: 'provisionCraneDetails', label: 'Provision Crane Details', type: 'textarea'},
      {key: 'ballastPumpDetails', label: 'Ballast Pump Details', type: 'textarea'},
      {key: 'bilgePumpDetails', label: 'Bilge Pump Details', type: 'textarea'},
      {key: 'firePumpDetails', label: 'Fire Pump Details', type: 'textarea'},
      {key: 'otherPumpDetails', label: 'Other Pump Details', type: 'textarea'},
    ],
  },
  {
    key: 'safety',
    label: 'Lifesaving & Firefighting',
    fields: [
      {key: 'lifeboatDetails', label: 'Lifeboat Details', type: 'textarea'},
      {key: 'rescueBoatDetails', label: 'Rescue Boat Details', type: 'textarea'},
      {key: 'lifeRaftDetails', label: 'Life Raft Details', type: 'textarea'},
      {key: 'lifeSavingApplianceNotes', label: 'Life Saving Appliance Notes', type: 'textarea'},
      {key: 'engineRoomFireSystem', label: 'Engine Room Fire System', type: 'textarea'},
      {key: 'deckFireSystem', label: 'Deck Fire System', type: 'textarea'},
      {key: 'paintStoreFireSystem', label: 'Paint Store Fire System', type: 'textarea'},
      {key: 'otherFirefightingDetails', label: 'Other Firefighting Details', type: 'textarea'},
    ],
  },
  {
    key: 'communication',
    label: 'Communication',
    fields: [
      {key: 'vesselPhoneNumber', label: 'Vessel Phone Number', type: 'text'},
      {key: 'vesselEmailAddress', label: 'Vessel Email Address', type: 'text'},
      {key: 'faxNumber', label: 'Fax Number', type: 'text'},
      {key: 'inmarsatC', label: 'Inmarsat C', type: 'text'},
      {key: 'mmsi', label: 'MMSI', type: 'text', readOnly: true},
      {key: 'iridiumNumber', label: 'Iridium Number', type: 'text'},
      {key: 'vsatDetails', label: 'VSAT Details', type: 'text'},
      {key: 'otherCommunicationDetails', label: 'Other Communication Details', type: 'textarea'},
    ],
  },
  {
    key: 'additional',
    label: 'Additional Information',
    fields: [
      {key: 'deckEquipmentDetails', label: 'Deck Equipment Details', type: 'textarea'},
      {key: 'navigationEquipmentDetails', label: 'Navigation Equipment Details', type: 'textarea'},
      {key: 'accommodationDetails', label: 'Accommodation Details', type: 'textarea'},
      {key: 'operationalLimitations', label: 'Operational Limitations', type: 'textarea'},
      {key: 'additionalParticulars', label: 'Additional Particulars', type: 'textarea'},
      {key: 'remarks', label: 'Remarks', type: 'textarea'},
    ],
  },
]
